/**
 * Geofences — Zonas de obra
 *
 * MapLibre draw: implementacion nativa con click handlers (no @mapbox/mapbox-gl-draw).
 * Decision: @mapbox/mapbox-gl-draw v1.4 requiere fork especifico para MapLibre v4 (maplibre-gl-draw
 * en npm) y aun tiene breaking changes sin resolver. Para el MVP se usa un polygon-draw
 * nativo: clic agrega punto, doble-clic cierra el poligono y abre el modal de guardado.
 * Esto evita 1 dependencia extra y es suficiente para crear zonas rectangulares/irregulares.
 */

import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import maplibregl from 'maplibre-gl'
import { geofencesApi, type Geofence, type GeofenceInput, type GeofencePolygon } from '../api/geofences'
import { Modal } from '../components/ui/Modal'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_KEY as string | undefined
const STYLE_URL = MAPTILER_KEY
  ? `https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_KEY}`
  : 'https://demotiles.maplibre.org/style.json'

const PRIMARY = '#2B6CFF'

function bboxFromCoords(coords: number[][]): [number, number, number, number] {
  const lngs = coords.map((c) => c[0])
  const lats = coords.map((c) => c[1])
  return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)]
}

// ---------------------------------------------------------------------------
// Form schema
// ---------------------------------------------------------------------------

const geofenceFormSchema = z.object({
  nombre: z.string().min(1, 'Nombre requerido'),
  horarioInicio: z.string().optional(),
  horarioFin: z.string().optional(),
})
type GeofenceForm = z.infer<typeof geofenceFormSchema>

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function Geofences() {
  const qc = useQueryClient()
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)

  // Draw state
  const [drawing, setDrawing] = useState(false)
  const drawPoints = useRef<number[][]>([])
  const drawMarkers = useRef<maplibregl.Marker[]>([])
  const previewLayerAdded = useRef(false)

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [editingGeofence, setEditingGeofence] = useState<Geofence | null>(null)
  const pendingPolygonRef = useRef<GeofencePolygon | null>(null)

  const { data: geofences, isLoading } = useQuery({
    queryKey: ['geofences'],
    queryFn: () => geofencesApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: (d: GeofenceInput) => geofencesApi.create(d),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['geofences'] }); closeModal() },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<GeofenceInput> }) =>
      geofencesApi.update(id, data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['geofences'] }); closeModal() },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => geofencesApi.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['geofences'] }),
  })

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, setValue } =
    useForm<GeofenceForm>({ resolver: zodResolver(geofenceFormSchema) })

  function closeModal() {
    setModalOpen(false)
    setEditingGeofence(null)
    pendingPolygonRef.current = null
    reset()
  }

  // -------------------------------------------------------------------------
  // Map init
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: STYLE_URL,
      center: [-63.18, -17.78],
      zoom: 11,
    })
    map.addControl(new maplibregl.NavigationControl(), 'top-right')
    mapRef.current = map

    map.on('load', () => {
      // Geofence fill layer
      map.addSource('geofences', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id: 'geofences-fill',
        type: 'fill',
        source: 'geofences',
        paint: { 'fill-color': PRIMARY, 'fill-opacity': 0.25 },
      })
      map.addLayer({
        id: 'geofences-outline',
        type: 'line',
        source: 'geofences',
        paint: { 'line-color': PRIMARY, 'line-width': 2 },
      })
      map.addLayer({
        id: 'geofences-label',
        type: 'symbol',
        source: 'geofences',
        layout: {
          'text-field': ['get', 'nombre'],
          'text-size': 12,
          'text-anchor': 'center',
        },
        paint: { 'text-color': PRIMARY, 'text-halo-color': '#fff', 'text-halo-width': 1.5 },
      })

      // Preview draw layer
      map.addSource('draw-preview', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id: 'draw-preview-fill',
        type: 'fill',
        source: 'draw-preview',
        paint: { 'fill-color': '#F59E0B', 'fill-opacity': 0.25 },
      })
      map.addLayer({
        id: 'draw-preview-line',
        type: 'line',
        source: 'draw-preview',
        paint: { 'line-color': '#F59E0B', 'line-width': 2, 'line-dasharray': [2, 2] },
      })
      previewLayerAdded.current = true
    })

    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [])

  // -------------------------------------------------------------------------
  // Sync geofences to map layer
  // -------------------------------------------------------------------------

  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return

    const source = map.getSource('geofences') as maplibregl.GeoJSONSource | undefined
    if (!source) return

    source.setData({
      type: 'FeatureCollection',
      features: (geofences ?? []).map((g) => ({
        type: 'Feature',
        id: g.id,
        properties: { nombre: g.nombre },
        geometry: g.poligono,
      })),
    })
  }, [geofences])

  // -------------------------------------------------------------------------
  // Draw handlers
  // -------------------------------------------------------------------------

  function startDrawing() {
    const map = mapRef.current
    if (!map) return
    setDrawing(true)
    drawPoints.current = []
    clearDrawMarkers()
    updatePreview([])
    map.getCanvas().style.cursor = 'crosshair'
  }

  function cancelDrawing() {
    const map = mapRef.current
    setDrawing(false)
    drawPoints.current = []
    clearDrawMarkers()
    updatePreview([])
    if (map) map.getCanvas().style.cursor = ''
  }

  function clearDrawMarkers() {
    drawMarkers.current.forEach((m) => m.remove())
    drawMarkers.current = []
  }

  function updatePreview(pts: number[][]) {
    const map = mapRef.current
    if (!map || !previewLayerAdded.current) return
    const source = map.getSource('draw-preview') as maplibregl.GeoJSONSource | undefined
    if (!source) return

    if (pts.length < 2) {
      source.setData({ type: 'FeatureCollection', features: [] })
      return
    }

    const closed = [...pts, pts[0]]
    source.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [closed] },
        },
      ],
    })
  }

  // Attach/detach map click handlers when drawing mode changes
  useEffect(() => {
    const map: maplibregl.Map | null = mapRef.current
    if (!map) return
    const safeMap = map

    function onMapClick(e: maplibregl.MapMouseEvent) {
      if (!drawing) return
      const pt = [e.lngLat.lng, e.lngLat.lat]
      drawPoints.current.push(pt)

      // Add dot marker
      const el = document.createElement('div')
      el.className = 'w-3 h-3 rounded-full bg-yellow-500 border-2 border-white shadow'
      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([pt[0], pt[1]])
        .addTo(safeMap)
      drawMarkers.current.push(marker)

      updatePreview(drawPoints.current)
    }

    function onMapDblClick(e: maplibregl.MapMouseEvent) {
      if (!drawing) return
      e.preventDefault()
      const pts = drawPoints.current
      if (pts.length < 3) {
        alert('Dibuja al menos 3 puntos para cerrar el poligono.')
        return
      }

      const polygon: GeofencePolygon = {
        type: 'Polygon',
        coordinates: [[...pts, pts[0]]],
      }
      pendingPolygonRef.current = polygon

      // Clean draw state
      cancelDrawing()

      // Open modal
      reset({ horarioInicio: '06:00', horarioFin: '18:00' })
      setModalOpen(true)
    }

    safeMap.on('click', onMapClick)
    safeMap.on('dblclick', onMapDblClick)

    return () => {
      safeMap.off('click', onMapClick)
      safeMap.off('dblclick', onMapDblClick)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawing])

  // -------------------------------------------------------------------------
  // Fit bounds on geofence item click
  // -------------------------------------------------------------------------

  function flyToGeofence(g: Geofence) {
    const map = mapRef.current
    if (!map) return
    const coords = g.poligono.coordinates[0]
    if (!coords || coords.length === 0) return
    const [w, s, e, n] = bboxFromCoords(coords)
    map.fitBounds([w, s, e, n], { padding: 80, duration: 600 })
  }

  // -------------------------------------------------------------------------
  // Form submit
  // -------------------------------------------------------------------------

  async function onSubmit(values: GeofenceForm) {
    if (editingGeofence) {
      await updateMutation.mutateAsync({
        id: editingGeofence.id,
        data: {
          nombre: values.nombre,
          horarioInicio: values.horarioInicio || undefined,
          horarioFin: values.horarioFin || undefined,
        },
      })
    } else {
      if (!pendingPolygonRef.current) return
      await createMutation.mutateAsync({
        nombre: values.nombre,
        poligono: pendingPolygonRef.current,
        horarioInicio: values.horarioInicio || undefined,
        horarioFin: values.horarioFin || undefined,
      })
    }
  }

  function openEdit(g: Geofence) {
    setEditingGeofence(g)
    setValue('nombre', g.nombre)
    setValue('horarioInicio', g.horarioInicio ?? '06:00')
    setValue('horarioFin', g.horarioFin ?? '18:00')
    setModalOpen(true)
  }

  function handleDelete(id: string) {
    if (!window.confirm('Eliminar esta zona de obra?')) return
    deleteMutation.mutate(id)
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Zonas de obra</h1>
        {!drawing ? (
          <Button size="sm" onClick={startDrawing}>+ Dibujar zona</Button>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-sm text-yellow-700 dark:text-yellow-400 font-medium animate-pulse">
              Clic: agregar punto | Doble-clic: cerrar poligono
            </span>
            <Button size="sm" variant="secondary" onClick={cancelDrawing}>Cancelar</Button>
          </div>
        )}
      </div>

      <div className="flex gap-4 h-[600px]">
        {/* Map */}
        <div ref={mapContainerRef} className="flex-1 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm" />

        {/* Sidebar list */}
        <div className="w-72 shrink-0 flex flex-col gap-2 overflow-y-auto">
          {isLoading && (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 rounded-xl bg-slate-200 dark:bg-slate-700 animate-pulse" />
              ))}
            </div>
          )}

          {!isLoading && (geofences ?? []).length === 0 && (
            <div className="text-center text-slate-400 text-sm mt-10">
              <p className="text-2xl mb-2">🗺</p>
              <p>Sin zonas registradas.</p>
              <p>Usa "+ Dibujar zona" para crear una.</p>
            </div>
          )}

          {(geofences ?? []).map((g) => (
            <div
              key={g.id}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => flyToGeofence(g)}
            >
              <p className="font-medium text-sm text-slate-900 dark:text-slate-100 truncate">{g.nombre}</p>
              {(g.horarioInicio || g.horarioFin) && (
                <p className="text-xs text-slate-500 mt-0.5">
                  {g.horarioInicio ?? '—'} – {g.horarioFin ?? '—'}
                </p>
              )}
              <div className="flex gap-2 mt-2">
                <button
                  onClick={(e) => { e.stopPropagation(); openEdit(g) }}
                  className="text-xs text-primary hover:underline"
                >
                  Editar
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(g.id) }}
                  className="text-xs text-danger hover:underline"
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create / Edit modal */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingGeofence ? 'Editar zona de obra' : 'Nueva zona de obra'}
        size="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            {...register('nombre')}
            label="Nombre"
            placeholder="Obra Norte — Sector A"
            error={errors.nombre?.message}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input {...register('horarioInicio')} type="time" label="Horario inicio" />
            <Input {...register('horarioFin')} type="time" label="Horario fin" />
          </div>

          {(createMutation.isError || updateMutation.isError) && (
            <p className="text-xs text-danger">Error al guardar. Verifique conexion.</p>
          )}

          <div className="flex gap-3 justify-end pt-1">
            <Button variant="secondary" type="button" onClick={closeModal}>Cancelar</Button>
            <Button type="submit" loading={isSubmitting}>Guardar</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
